import { nativeAuthHeaders } from "./apiBase";

function readableResponse(response: Response): Response {
  const readJson = response.json.bind(response);
  response.json = async () => {
    try {
      return await readJson();
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") throw error;
      // Do not include an HTML/body fragment in an error reported to Sentry.
      throw new Error(
        "The server response could not be read. Please try again."
      );
    }
  };
  return response;
}

function networkError(cause: unknown): Error {
  const offline =
    typeof navigator !== "undefined" && navigator.onLine === false;
  return new Error(
    offline
      ? "You're offline. Please reconnect and try again."
      : "Your connection was interrupted. Please check your connection and try again.",
    { cause }
  );
}

/** Keep transport failures out of the tRPC/SuperJSON wire protocol. */
export async function trpcFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const headers = new Headers(init?.headers);
  for (const [name, value] of Object.entries(nativeAuthHeaders()))
    headers.set(name, value);
  const method =
    init?.method ?? (input instanceof Request ? input.method : "GET");
  // A failed mutation may already have committed. Never replay it here.
  const canRetry = method.toUpperCase() === "GET";
  const attempt = () =>
    globalThis.fetch(input, {
      ...(init ?? {}),
      headers,
      credentials: "include",
    });
  try {
    const response = await attempt();
    if (canRetry && response.status === 503) {
      try {
        return readableResponse(await attempt());
      } catch (error) {
        if (
          (error instanceof Error && error.name === "AbortError") ||
          init?.signal?.aborted
        )
          throw error;
        // Preserve the original valid server error if the retry cannot connect.
      }
    }
    return readableResponse(response);
  } catch (error) {
    if (
      (error instanceof Error && error.name === "AbortError") ||
      init?.signal?.aborted
    )
      throw error;
    const message = error instanceof Error ? error.message : String(error);
    if (canRetry && message.includes("did not match the expected pattern")) {
      try {
        return readableResponse(await attempt());
      } catch (retryError) {
        if (
          (retryError instanceof Error && retryError.name === "AbortError") ||
          init?.signal?.aborted
        )
          throw retryError;
        throw networkError(retryError);
      }
    }
    if (
      message.includes("did not match the expected pattern") ||
      message.includes("Failed to fetch") ||
      message.includes("NetworkError") ||
      message.includes("Load failed") ||
      (typeof navigator !== "undefined" && navigator.onLine === false)
    )
      throw networkError(error);
    throw error;
  }
}
